import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Formik } from 'formik';
import propTypes from 'prop-types';
import { useContext, useEffect, useState } from 'react';
import {
  ATTACH_LABEL,
  ATTACH_LABEL_DESCRIPTION,
  ATTACH_LABEL_NOTE,
  BE_IN_TOUCH,
  BE_IN_TOUCH_DESCRIPTION,
  EXCHANGE_PLEASE,
  GENERIC_FAILURE,
  MERCHANT_ASSESMENT,
  MERCHANT_ASSESMENT_DESCRIPTION,
  MERCHANT_ASSESMENT_NO_RESPONSE,
  MERCHANT_NOTIFIED,
  PLEASE_NOTE,
  PLEASE_PROVIDE_MORE,
  REASON,
  REQUIRED_FIELD,
  SUBMIT,
  TELL_US,
  VIEW_RETURNS_LABEL,
} from '../../config/text/text';
import ErrorTrackingService from '../../helpers/errorTrackingService';
import { openPdf } from '../../helpers/pdf';
import {
  convertReasonToGenericReason,
  getReturnsFormInitialData,
  hasFormBeenSubmitted,
  RETURN_REASONS,
  storeFormData,
  validateReturnsForm,
  generatReturnsApiData,
  getShowOnlyContactStep,
  getReturnsFormStoredData,
} from '../../helpers/returns';
import ThemeContext from '../../providers/ThemeProvider';
import {
  retreiveReturnsLabelPdf,
  submitReturnsForm,
} from '../../services/returns';
import Button from '../_generic/button/CTAButton';
import LinkButton from '../_generic/button/LinkButton';
import FormikSelect from '../_generic/input/FormikSelect';
import FormikTextarea from '../_generic/input/FormikTextarea';
import Spinner from '../_generic/spinner/Spinner';
import Step from '../_generic/stepper/Step';
import Stepper from '../_generic/stepper/Stepper';
import useToast from '../_generic/toast/UseToast';

const VoucherReturnsForm = ({ voucher, isOpen }) => {
  const theme = useContext(ThemeContext);
  const toast = useToast();
  const [pdf, setPdf] = useState({ arrayBuffer: null, href: null });
  const [submitted, setSubmitted] = useState(() => {
    return hasFormBeenSubmitted(voucher.voucherCode);
  });

  // on init, load pdf if the form has been submitted
  useEffect(() => {
    const getPdf = async () => {
      const values = getReturnsFormStoredData(voucher.voucherCode);
      // values present when form has been submitted
      if (values) {
        const result = await retreiveReturnsLabelPdf({
          details: generatReturnsApiData({ values }),
          orderLineId: voucher.orderLineId,
        });
        setPdf(result);
      }
    };

    if (isOpen && !pdf.arrayBuffer && !pdf.href) {
      getPdf();
    }
  }, [isOpen, pdf, voucher.orderLineId, voucher.voucherCode]);

  const sendForm = async (values) => {
    try {
      const result = await submitReturnsForm({
        details: generatReturnsApiData({ values }),
        orderLineId: voucher.orderLineId,
      });
      setPdf(result);
      storeFormData(voucher.voucherCode, values);
      setSubmitted(true);
    } catch (error) {
      toast.addToast(GENERIC_FAILURE, 'toast-error');
      ErrorTrackingService.logError(error);
    }
  };

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={getReturnsFormInitialData(voucher.voucherCode)}
        onSubmit={sendForm}
        validate={validateReturnsForm}
        validateOnBlur
        validateOnChange
        validateOnMount
      >
        {({
          handleChange,
          handleSubmit,
          touched,
          handleBlur,
          isSubmitting,
          isValid,
          values,
          errors,
        }) => {
          const genericReason = convertReasonToGenericReason(values.reason);
          const showContactStep = getShowOnlyContactStep({
            genericReason,
            voucher,
          });

          return (
            <>
              {isSubmitting && <Spinner display="absolute" />}
              <Stepper>
                <Step step="1" title={TELL_US}>
                  <form name="send-gift-form" onSubmit={handleSubmit}>
                    <FormikSelect
                      disable={submitted}
                      errorText={errors.reason}
                      inputError={errors.reason}
                      inputTouched={touched.reason}
                      inputValue={values.reason}
                      name="reason"
                      onBlurFunction={handleBlur}
                      onChangeFunction={handleChange}
                      options={RETURN_REASONS}
                      placeholder={`${REASON}*`}
                      required
                    />
                    <FormikTextarea
                      disable={submitted}
                      errorText={errors.information}
                      inputError={errors.information}
                      inputTouched={touched.information}
                      inputValue={values.information}
                      maxLength={150}
                      name="information"
                      onBlurFunction={handleBlur}
                      onChangeFunction={handleChange}
                      placeholder={`${PLEASE_PROVIDE_MORE}${
                        values.reason === 'other' ? '*' : ''
                      }`}
                      required={values.reason === 'other'}
                    />
                    <label
                      className="checkbox-container"
                      htmlFor={`exchange-${voucher.voucherCode}`}
                    >
                      <input
                        checked={values.exchange}
                        className="checkbox"
                        disabled={submitted}
                        id={`exchange-${voucher.voucherCode}`}
                        name="exchange"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        type="checkbox"
                      />
                      {EXCHANGE_PLEASE}
                    </label>
                    <p className="required">*{REQUIRED_FIELD}</p>
                    {!submitted && (
                      <Button
                        disabled={!isValid || isSubmitting}
                        primaryButton
                        type="submit"
                      >
                        {SUBMIT}
                      </Button>
                    )}
                    {submitted && (
                      <p className="notified">
                        <span className="notified-icon">
                          <FontAwesomeIcon icon={faCheck} />
                        </span>
                        {MERCHANT_NOTIFIED}
                      </p>
                    )}
                  </form>
                </Step>
                {submitted && showContactStep && (
                  <Step step="2" title={BE_IN_TOUCH}>
                    <p className="no-margin">{BE_IN_TOUCH_DESCRIPTION}</p>
                  </Step>
                )}
                {submitted && !showContactStep && (
                  <>
                    <Step step="2" title={ATTACH_LABEL}>
                      <p>{ATTACH_LABEL_DESCRIPTION}</p>
                      {pdf.href && (
                        <LinkButton className="margin" href={pdf.href}>
                          {VIEW_RETURNS_LABEL}
                        </LinkButton>
                      )}
                      {pdf.arrayBuffer && (
                        <Button
                          className="margin"
                          onClick={openPdf(pdf.arrayBuffer, 'returns')}
                        >
                          {VIEW_RETURNS_LABEL}
                        </Button>
                      )}
                      <p className="no-margin">
                        <span className="bold">{PLEASE_NOTE}: </span>
                        {ATTACH_LABEL_NOTE}
                      </p>
                    </Step>
                    <Step step="3" title={MERCHANT_ASSESMENT}>
                      <p>{MERCHANT_ASSESMENT_DESCRIPTION}</p>
                      <p className="no-margin">
                        <span>{MERCHANT_ASSESMENT_NO_RESPONSE} </span>
                        <a href="mailto:cs@dealhelp.co.uk">cs@dealhelp.co.uk</a>
                      </p>
                    </Step>
                  </>
                )}
              </Stepper>
            </>
          );
        }}
      </Formik>
      <style jsx>{`
        .required {
          font-size: 0.8em;
          text-align: right;
          margin: 0;
        }
        .checkbox-container {
          margin-bottom: 15px;
        }
        .checkbox {
          margin-right: 8px;
        }
        .notified {
          color: ${theme.colors.socialcuecolor.assure};
          font-weight: bold;
        }
        .notified-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          background-color: ${theme.colors.socialcuecolor.assure};
          height: 40px;
          width: 40px;
          margin-right: 10px;
          border-radius: 50%;
        }
        .no-margin {
          margin: 0;
        }
        a {
          color: ${theme.colors.primary};
        }
        .bold {
          font-weight: bold;
          font-size: 0.9em;
        }
      `}</style>
    </>
  );
};

VoucherReturnsForm.propTypes = {
  isOpen: propTypes.bool,
  voucher: propTypes.object,
};

export default VoucherReturnsForm;
